package com.dotdrives.web.pages.forms

import com.dotdrives.controller.FormBuilderController
import com.dotdrives.controller.FormBuilderUiState
import kotlinx.coroutines.*
import kotlin.js.Promise
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.input
import react.dom.html.ReactHTML.img
import com.dotdrives.model.safeGivingUrl
import react.dom.html.ReactHTML.label
import react.dom.html.ReactHTML.span
import web.cssom.ClassName
import web.html.InputType
import web.html.file
import web.dom.document

external interface FormLogoUploadProps : Props {
    var state: FormBuilderUiState
    var controller: FormBuilderController
    var canWrite: Boolean
}

val FormLogoUpload = FC<FormLogoUploadProps> { props ->
    val (scope) = useState { MainScope() }
    val (readError, setReadError) = useState<String?>(null)
    val (reading, setReading) = useState(false)
    val (failedLogo, setFailedLogo) = useState<String?>(null)
    useEffect(Unit) { try { awaitCancellation() } finally { scope.cancel() } }
    val state = props.state
    div {
        className = ClassName("v2-logo-upload")
        span { className = ClassName("v2-logo-heading"); +"Organization logo" }
        div {
            className = ClassName("v2-logo-card")
            div {
                className = ClassName("v2-logo-thumbnail")
                val logo = state.draftDesign.logoUrl
                if (logo != null && logo != failedLogo && safeGivingUrl(logo)) img {
                    src = logo; alt = "Current organization logo"; onError = { setFailedLogo(logo) }
                } else span { ariaHidden = true; +"◉" }
            }
            div {
                className = ClassName("v2-logo-info")
                span { +(state.draftDesign.logoFileName ?: if (state.draftDesign.logoUrl != null) "Current logo" else "No logo uploaded") }
                span { +"PNG, JPG or WebP · Up to 2 MB" }
            }
            label {
                className = ClassName("v2-logo-choose" + if (!props.canWrite || state.saving || state.uploadingLogo || reading) " disabled" else "")
                +if (reading || state.uploadingLogo) "Uploading…" else "Upload new"
            input {
                type = InputType.file
                accept = "image/png,image/jpeg,image/webp"
                ariaLabel = "Upload organization logo"
                disabled = !props.canWrite || state.saving || state.uploadingLogo || reading
                onChange = { event ->
                    val file = event.currentTarget.files?.item(0)
                    event.currentTarget.value = ""
                    if (file != null && props.canWrite && !state.saving && !state.uploadingLogo && !reading) {
                        setReadError(null)
                        if (file.size.toDouble() > 2 * 1024 * 1024 || file.type !in listOf("image/png", "image/jpeg", "image/webp")) {
                            setReadError("Choose a PNG, JPEG or WebP image up to 2 MB.")
                        } else {
                            val formId = state.form?.id
                            setReading(true)
                            scope.launch {
                                try {
                                    val bytes = prepareFormLogo(file.asDynamic(), file.type)
                                    if (props.controller.state.value.form?.id == formId) props.controller.uploadLogo(file.name, file.type, bytes)
                                } catch (error: Throwable) {
                                    if (error is CancellationException) throw error
                                    setReadError("This image could not be processed. Choose a valid PNG, JPEG or WebP up to 2 MB.")
                                } finally { setReading(false) }
                            }
                        }
                    }
                }
            }
        }
        }
        span {
            className = ClassName("v2-logo-help")
            +when {
                reading || state.uploadingLogo -> "Uploading logo…"
                state.draftDesign.logoFileName != null -> if (state.draftDesign != state.form?.config?.design) "Save changes to apply this logo." else "Logo saved."
                state.draftDesign.logoUrl != null -> "A logo is attached. Choose an image to replace it."
                else -> "PNG, JPEG or WebP · Up to 2 MB. Save changes to apply."
            }
        }
        readError?.let { div { role = react.dom.aria.AriaRole.alert; +it } }
    }
}

/** Re-encode decoded pixels, dropping source metadata and rejecting malformed image content. */
internal suspend fun prepareFormLogo(file: dynamic, mime: String): ByteArray {
    val bitmap = js("createImageBitmap(file)").unsafeCast<Promise<dynamic>>().await()
    try {
        val width = bitmap.width as Int
        val height = bitmap.height as Int
        require(width > 0 && height > 0 && width.toLong() * height <= 40_000_000) { "Image dimensions are too large." }
        val ratio = minOf(1.0, 2048.0 / maxOf(width, height))
        val canvas = document.createElement("canvas").asDynamic()
        canvas.width = maxOf(1, (width * ratio).toInt())
        canvas.height = maxOf(1, (height * ratio).toInt())
        canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height)
        val encoded = Promise<dynamic> { resolve, reject ->
            val callback: (dynamic) -> Unit = { blob ->
                if (blob == null) reject(IllegalArgumentException("Image encoding failed.")) else resolve(blob)
            }
            canvas.toBlob(callback, mime, .9)
        }.await()
        require(encoded.type == mime && (encoded.size as Int) <= 2 * 1024 * 1024) { "Encoded image is too large." }
        val buffer = encoded.arrayBuffer().unsafeCast<Promise<dynamic>>().await()
        val data = js("new Uint8Array(buffer)")
        return ByteArray(data.length as Int) { (data[it] as Int).toByte() }
    } finally { bitmap.close() }
}
